import { normalizeWorldDescriptor, randomIntRNG } from "../helpers";
import { EntityData, ObjectData } from "../types";
import Chunk from "./chunk";
import seedrandom from 'seedrandom';

class ChunkFactory{
    //this method generates a new chunk based on the default world description
    //Example of default world description (world.json):
    // {
    // 	"defaultTile" : "1",
    // 	"liquid" : "empty",
    // 	"naturalObjects" : [
    // 		{"name": "grass", "chance": 0.8},
    // 		{"name": "tree1", "chance": 0.1},
    // 		{"name": "rock",  "chance": 0.05}
    // 	],
    // 	"naturalEntities" : [
    // 		{"name": "chicken", "chance": 0.5},
    // 		{"name": "coin",    "chance": 0.25}
    // 	]
    // }
    //
    // "chance" is the probability (0..1) that a single spawn attempt succeeds.
    // Every naturalObjects entry gets OBJECT_SPAWN_ATTEMPTS attempts per chunk
    // (so expected count = chance * OBJECT_SPAWN_ATTEMPTS) and every
    // naturalEntities entry gets ENTITY_SPAWN_ATTEMPTS attempts per chunk.
    //
    // The old format (a plain string array where duplicates raised the odds)
    // is still supported - it is converted by normalizeWorldDescriptor().

    static OBJECT_SPAWN_ATTEMPTS = 10; //per naturalObjects entry, per chunk
    static ENTITY_SPAWN_ATTEMPTS = 1;  //per naturalEntities entry, per chunk

    //entity names that are items and need type "item" + variant to spawn
    static ITEM_ENTITY_NAMES = ["coin", "heart", "egg", "mistery"];

    static generateDefaultChunk(data: any, world:string, x: number, y: number): Chunk{
        let rng = seedrandom(`${world}_${x}_${y}`);
        let descriptor = normalizeWorldDescriptor(data);

        //natural objects - each entry rolls its own spawn attempts
        let objects: ObjectData[] = [];
        for(let entry of descriptor.naturalObjects){
            for(let i = 0; i < ChunkFactory.OBJECT_SPAWN_ATTEMPTS; i++){
                let roll = rng();
                let objX = randomIntRNG(rng, 0, Chunk.CHUNK_SIZE);
                let objY = randomIntRNG(rng, 0, Chunk.CHUNK_SIZE);
                if(roll < entry.chance){
                    objects.push({
                        x: objX,
                        y: objY,
                        name: entry.name
                    });
                }
            }
        }

        //natural entities - same idea, but with proper type/variant data
        //so the game can actually spawn them when the chunk is unpacked
        let entities: EntityData[] = [];
        for(let entry of descriptor.naturalEntities){
            for(let i = 0; i < ChunkFactory.ENTITY_SPAWN_ATTEMPTS; i++){
                let roll = rng();
                let entX = randomIntRNG(rng, 0, Chunk.CHUNK_SIZE);
                let entY = randomIntRNG(rng, 0, Chunk.CHUNK_SIZE);
                if(roll < entry.chance){
                    let isItem = ChunkFactory.ITEM_ENTITY_NAMES.includes(entry.name);
                    entities.push({
                        x: entX,
                        y: entY,
                        name: entry.name,
                        type: isItem ? "item" : entry.name,
                        variant: isItem ? entry.name : "",
                        data: null
                    });
                }
            }
        }

        let chunkData = {
            tileImage: descriptor.defaultTile,
            liquid: descriptor.liquid ?? "empty",
            objects: objects,
            entities: entities,
            areas: [],
            portals: []
        };

        let chunk = new Chunk(world, x, y, chunkData);
        chunk.isDefault = true;

        return chunk;
    }
}

export default ChunkFactory;
